// Wave 14+ / Rapha Fit — Compression visualization.
//
// Renders the rider's body hull as an interactive 3D mesh, colored
// per-vertex by the compression value at each vertex's Y coordinate.
// Drag to rotate. Size strip on top re-pulls the compression series
// in place when re-picked.
//
// Color stops:
//   compression ≥ +5%   → red (too tight)
//   -2% .. +5%          → brand green (on-fit)
//   compression ≤ -2%   → blue (loose)
//
// Falls back to a flat info card if no hull mesh was retained (e.g. the
// rider navigated back from the report and the in-memory hull was
// dropped on remount).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import { colors } from '../theme';

interface CompressionPoint {
  y_cm: number;
  target_cm: number;
  rider_cm: number;
  compression: number;
}

interface CatalogSize {
  name: string;
  chest_cm?: number;
  waist_cm?: number;
  hip_cm?: number;
  thigh_cm?: number;
}

interface CatalogGarment {
  id: string;
  category: string;
  name: string;
  sizes: CatalogSize[];
  fit_offset_pct: number;
}

interface HullMesh {
  positions: Float32Array;
  indices: Uint16Array | Uint32Array;
  normals: Float32Array;
}

interface Props {
  garmentId: string;
  size: string;
  hullMesh: HullMesh | null;
  riderHeightCm: number | null;
  onChangeSize: (size: string) => void;
  onClose: () => void;
}

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

function compressionColorRGB(c: number): [number, number, number] {
  // Match compressionColor() in theme — return linear-RGB triplets for
  // the Three.js BufferAttribute (each in [0, 1]).
  // c >= 0.05: red (#ff4757)
  // -0.02 < c < 0.05: brand (#6aff45)
  // c <= -0.02: blue/data (#00d9ff)
  if (c >= 0.05) return [1.0, 0.278, 0.341];
  if (c <= -0.02) return [0.0, 0.851, 1.0];
  return [0.416, 1.0, 0.271];
}

export default function CompressionMap({
  garmentId,
  size,
  hullMesh,
  riderHeightCm,
  onChangeSize,
  onClose,
}: Props) {
  const { t } = useTranslation('rapha-fit');
  const [points, setPoints] = useState<CompressionPoint[]>([]);
  const [garment, setGarment] = useState<CatalogGarment | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const colorAttrRef = useRef<THREE.BufferAttribute | null>(null);
  const rotRef = useRef({ y: 0, dragging: false, lastX: 0 });

  // Load the catalog once to drive the size strip.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/fit/catalog`, {
      headers: { Authorization: `Bearer ${token ?? ''}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((catalog: CatalogGarment[]) => {
        const g = catalog.find((x) => x.id === garmentId) ?? null;
        setGarment(g);
      })
      .catch(() => {});
  }, [garmentId]);

  // Fetch compression series whenever garment + size change.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/fit/compression`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token ?? ''}`,
      },
      body: JSON.stringify({ garmentId, size }),
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((pts: CompressionPoint[]) => setPoints(pts))
      .catch(() => setPoints([]));
  }, [garmentId, size]);

  // Three.js scene setup (once).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hullMesh) return;
    const W = container.clientWidth || 320;
    const H = container.clientHeight || 420;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0e1a);
    scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const key = new THREE.DirectionalLight(0xffffff, 0.45);
    key.position.set(2, 3, 4);
    scene.add(key);

    const camera = new THREE.PerspectiveCamera(34, W / H, 0.05, 20);
    camera.position.set(0, 0.3, 2.4);
    camera.lookAt(0, 0.3, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(W, H);
    container.appendChild(renderer.domElement);

    // Build mesh from the hull data + an initial per-vertex color buffer
    // (white; we'll re-paint when the compression series lands).
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(hullMesh.positions, 3));
    geom.setAttribute('normal', new THREE.BufferAttribute(hullMesh.normals, 3));
    geom.setIndex(new THREE.BufferAttribute(hullMesh.indices, 1));
    const nVerts = hullMesh.positions.length / 3;
    const colorArr = new Float32Array(nVerts * 3);
    colorArr.fill(0.7);
    const colorAttr = new THREE.BufferAttribute(colorArr, 3);
    geom.setAttribute('color', colorAttr);
    colorAttrRef.current = colorAttr;

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      metalness: 0.0,
      roughness: 0.6,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.frustumCulled = false;
    // Re-center the mesh on the camera focal point (the hull world
    // origin sits at the rider's body centroid; shift so the mid-body
    // sits near 0,0,0).
    geom.computeBoundingBox();
    if (geom.boundingBox) {
      const center = new THREE.Vector3();
      geom.boundingBox.getCenter(center);
      mesh.position.set(-center.x, -center.y, -center.z);
    }
    scene.add(mesh);
    meshRef.current = mesh;
    sceneRef.current = scene;
    rendererRef.current = renderer;

    // Drag-to-rotate.
    const onDown = (e: MouseEvent) => {
      rotRef.current.dragging = true;
      rotRef.current.lastX = e.clientX;
    };
    const onUp = () => {
      rotRef.current.dragging = false;
    };
    const onMove = (e: MouseEvent) => {
      if (!rotRef.current.dragging) return;
      rotRef.current.y += (e.clientX - rotRef.current.lastX) * 0.012;
      rotRef.current.lastX = e.clientX;
    };
    renderer.domElement.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);

    let rafId = 0;
    const tick = () => {
      mesh.rotation.y = rotRef.current.y;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    };
    tick();

    const onResize = () => {
      if (!container) return;
      const w = container.clientWidth || 320;
      const h = container.clientHeight || 420;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
      renderer.domElement.removeEventListener('mousedown', onDown);
      renderer.dispose();
      geom.dispose();
      mat.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [hullMesh]);

  // Repaint vertex colors whenever the compression series changes.
  useEffect(() => {
    const mesh = meshRef.current;
    const colorAttr = colorAttrRef.current;
    if (!mesh || !colorAttr || !hullMesh || points.length === 0) return;
    const positions = hullMesh.positions;
    const colors_arr = colorAttr.array as Float32Array;
    const nVerts = positions.length / 3;
    // The hull's world Y measures from the rider's centroid. The
    // compression series y_cm measures from the feet. Riders are
    // roughly riderHeightCm tall; shift the per-vertex Y by half-height
    // so it lines up against the series.
    const halfHeightM = (riderHeightCm ?? 175) * 0.5 * 0.01;
    // Build a quick lookup of (y_cm sorted, compression).
    const sortedY = [...points].sort((a, b) => a.y_cm - b.y_cm);
    const findCompressionAtY = (yCm: number): number => {
      if (yCm <= sortedY[0].y_cm) return sortedY[0].compression;
      if (yCm >= sortedY[sortedY.length - 1].y_cm)
        return sortedY[sortedY.length - 1].compression;
      // Linear interpolate between bracketing points.
      for (let i = 1; i < sortedY.length; i++) {
        if (yCm <= sortedY[i].y_cm) {
          const lo = sortedY[i - 1];
          const hi = sortedY[i];
          const t = (yCm - lo.y_cm) / (hi.y_cm - lo.y_cm);
          return lo.compression * (1 - t) + hi.compression * t;
        }
      }
      return sortedY[sortedY.length - 1].compression;
    };
    for (let v = 0; v < nVerts; v++) {
      // World-space Y in metres → shift to "from feet" → cm.
      const worldY = positions[v * 3 + 1];
      const yCm = (worldY + halfHeightM) * 100;
      const c = findCompressionAtY(yCm);
      const [r, g, b] = compressionColorRGB(c);
      colors_arr[v * 3] = r;
      colors_arr[v * 3 + 1] = g;
      colors_arr[v * 3 + 2] = b;
    }
    colorAttr.needsUpdate = true;
  }, [points, hullMesh, riderHeightCm]);

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.borderFocus}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textMuted }}>
            {t('compression.title')}
          </Typography>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: colors.text }}>
            {garment?.name ?? t('compression.garment')}
          </Typography>
        </Box>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ color: colors.textMuted, borderColor: colors.border }}>
          {t('compression.back')}
        </Button>
      </Stack>

      <Typography sx={{ color: colors.textMuted, fontSize: '0.8125rem', mb: 2 }}>
        {t('compression.subtitle')}
      </Typography>

      {/* Size strip */}
      {garment && (
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
          {garment.sizes.map((s) => (
            <Chip
              key={s.name}
              label={s.name}
              onClick={() => onChangeSize(s.name)}
              sx={{
                cursor: 'pointer',
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                backgroundColor:
                  s.name === size ? 'rgba(106, 255, 69, 0.12)' : colors.surfaceElevated,
                border: `1px solid ${s.name === size ? colors.brand : colors.border}`,
                color: s.name === size ? colors.brand : colors.text,
                '&:hover': { borderColor: colors.borderFocus },
              }}
            />
          ))}
        </Stack>
      )}

      {/* Legend */}
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
        <LegendDot color={colors.danger} label={t('compression.tight')} />
        <LegendDot color={colors.brand} label={t('compression.onFit')} />
        <LegendDot color={colors.data} label={t('compression.loose')} />
      </Stack>

      {/* 3D hull canvas */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: 480,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: colors.bg,
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      />
      {!hullMesh && (
        <Typography sx={{ color: colors.textFaint, fontStyle: 'italic', mt: 2, fontSize: '0.8125rem' }}>
          The body model is only available right after a fit sweep. Remeasure to see the 3D compression view.
        </Typography>
      )}
    </Box>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
      <Typography sx={{ fontSize: '0.75rem', color: colors.textMuted }}>{label}</Typography>
    </Stack>
  );
}
